/**
 * Types + tunable tolerances for the RawFloorPlan → FloorPlanRoom converter.
 *
 * All tolerances are in FEET (the converter scales the raw meters input to feet
 * as its very first step, so every later stage works in output units).
 */

export const FT_PER_M = 3.280839895;

/** What the room is used for, when known. It steers what a scan can't show,
 *  like which way a door swings or whether a wide one is a closet bifold. */
export type RoomKind = 'bedroom' | 'hallway';

/**
 * The room kind a room's name implies, if any. The kind must be the name's last
 * word, ahead of any "(4)" count: "Upstairs Hall" is a hallway, "Hall Bath" is not.
 */
export const roomKindFromName = (name: string | undefined): RoomKind | undefined => {
  if (!name) return undefined;
  if (/\bbed ?room\s*(\(\d+\))?$/i.test(name.trim())) return 'bedroom';
  if (/\bhall(way)?\s*(\(\d+\))?$/i.test(name.trim())) return 'hallway';
  return undefined;
};

export interface ConversionOptions {
  /**
   * Grid-alignment fit (align.ts#normalizeFloorPlan, runs before anything
   * else): walls shorter than `gridAlignMinWallRatio * longestWall` are
   * excluded from the whole-room rotation fit.
   */
  gridAlignMinWallRatio: number;
  /**
   * Grid-alignment fit: a wall counts as an "inlier" for a candidate rotation
   * when its post-rotation deviation from the nearest grid axis is within
   * this many degrees. Coarser than `axisSnapAngleDeg` on purpose — this
   * picks the dominant wall cluster; `axisSnapAngleDeg` below does the fine
   * per-wall snap afterward, once the room is already rotated into place.
   */
  gridAlignInlierToleranceDeg: number;
  /** Walls/openings shorter than this are sensor noise — culled. */
  minSurfaceLength: number;
  /** A wall within this many degrees of an axis is snapped onto it. */
  axisSnapAngleDeg: number;
  /** Parallel axis wall-lines closer than this merge into one line. */
  lineClusterTol: number;
  /** Wall endpoints within this distance are considered joined. */
  joinTol: number;
  /** Max attachment error for a door/window/opening before it's discarded. */
  maxAttachError: number;
  /** Largest perimeter gap (between dead-end walls) the converter bridges with
   *  confidence. Every gap is bridged with synthetic walls, since the room
   *  can't close otherwise; a gap larger than this also gets a warning in the
   *  report asking for the wall to be reviewed. */
  maxGapBridge: number;
  /** Object rotations within this many degrees of a 90° multiple snap onto it;
   *  anything further keeps its exact rotation (FloorPlanFreeElement.rotation). */
  objectRotationSnapDeg: number;
  /** What the room is used for, when the caller knows (see RoomKind). */
  roomKind?: RoomKind;
}

export const DEFAULT_CONVERSION_OPTIONS: ConversionOptions = {
  gridAlignMinWallRatio: 0.25,
  gridAlignInlierToleranceDeg: 10,
  minSurfaceLength: 0.35,
  axisSnapAngleDeg: 7,
  lineClusterTol: 0.35,
  joinTol: 0.75,
  maxAttachError: 1.0,
  maxGapBridge: 6.0,
  objectRotationSnapDeg: 5,
};

/** What happened to every raw surface/object that didn't convert cleanly. */
export interface ConversionReport {
  /** Walls not used by the chosen perimeter loop (e.g. RoomPlan's synthesized alcove-mouth walls). */
  droppedWallIds: string[];
  /** Surfaces culled as sensor noise (shorter than minSurfaceLength). */
  culledSurfaceIds: string[];
  /** Doors/windows/openings with no perimeter surface within maxAttachError. */
  discardedOpeningIds: string[];
  /** Openings that coincide with an alcove mouth — implicit in the model, so dropped. */
  absorbedOpeningIds: string[];
  /** Anything odd a human should double-check. */
  warnings: string[];
}

export const emptyReport = (): ConversionReport => ({
  droppedWallIds: [],
  culledSurfaceIds: [],
  discardedOpeningIds: [],
  absorbedOpeningIds: [],
  warnings: [],
});
